import { useMediaQuery, useTheme } from '@mui/material';
import { Configuration, SuccessResponse_1OfGetConfigurationResponse } from '../../../../app/services/api/generated';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { useContext, useEffect, useState } from 'react';
import { DrawerNavItem } from '../interfaces/DrawerNavItem';
import { canAccessPageStatus, resolvePreviewSwap, shouldHideInDemo } from '../../../../utils/featureAccess';
import {
  UpdateOutlined, ManageAccountsOutlined, FastfoodOutlined, KitchenOutlined, LocalShippingOutlined,
  ScaleOutlined, MenuBookOutlined, SwapCallsOutlined,
  Checklist,
  CalendarViewWeek,
  Flaky,
  ChecklistRtl,
  Rule,
  LineStyle,
  AssignmentTurnedIn,
  FormatListBulleted,
  FolderOutlined,
  AssessmentOutlined, SchoolOutlined,
  AssignmentTurnedInOutlined, WorkOutlineOutlined, DashboardOutlined, SwapHoriz,
  StorefrontOutlined, FileCopyOutlined,
} from '@mui/icons-material';
import {
  ListIcon, Building05, PackageCheck,
  ShoppingCart01, CoinsSwap02, CoinsHand, BarChart08,
  Truck02, MarkerPin01, ReceiptCheck, FileCheck01, Trash03, Cube,
  Settings01,
} from '../../../../assets';
import { useNavigate } from 'react-router-dom';
import useLogError from '../../../../hooks/useLogError';
import { System } from '../../../../enums/System';
import { allReportPermissions } from '../../../../pages/Reporting/components/shared/ReportTypesData';


const appNavItems: DrawerNavItem[] = [
  /*  {
      canRender: false,
      translationKey: 'home',
      icon: <Home02 />,
      href: '/',
      systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
    }, */
  {
    canRender: false,
    translationKey: 'store',
    icon: <StorefrontOutlined />,
    href: '/store/storefront',
    permissions: ['StorefrontView'],
    systemIds: [System.OrderlyForStores,System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'catalogues',
    icon: <ListIcon />,
    href: '/catalogue-list',
    permissions: ['CataloguesView'],
    systemIds: [System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'retailers',
    icon: <Building05 />,
    href: '/retailers',
    permissions: ['RetailersView'],
    systemIds: [System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'orderList',
    icon: <ShoppingCart01 />,
    href: '/orders',
    permissions: ['OrdersView'],
    systemIds: [System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'deliveries',
    icon: <Truck02 />,
    href: '/deliveries',
    permissions: ['DeliveriesView'],
    systemIds: [System.OrderlyForDistributors],
  }/*,{
    canRender: true,
    translationKey: 'claims',
    icon: <FileCheck01 />,
    href: '/claims',
  }*/, {
    canRender: false,
    translationKey: 'finance',
    icon: <CoinsHand />,
    href: '/finance',
    permissions: ['FinanceView'],
    systemIds: [System.OrderlyForDistributors],
  }, {
    canRender: false,
    translationKey: 'reporting',
    icon: <BarChart08 />,
    permissions: [...allReportPermissions],
    systemIds: [System.OrderlyForDistributors],
    children: [
      {
        canRender: false,
        translationKey: 'reporting',
        icon: <BarChart08 />,
        href: '/reports',
        permissions: [...allReportPermissions],
        previewReplacementConfig: 'ReportsV2Enabled',
        systemIds: [System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'reportingV2',
        icon: <BarChart08 />,
        href: '/reports/v2',
        permissions: [...allReportPermissions],
        // Config-gated: only surfaces when the ReportsV2Enabled flag is true for the store.
        storePermission: 'ReportsV2Enabled',
        // Preview-gated too: non-production environments (or Admin) only until GA.
        status: 'preview',
        systemIds: [System.OrderlyForDistributors],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'orderStock',
    icon: <ShoppingCart01 />,
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'browseProducts',
        icon: <ListIcon />,
        permissions: ['OrderableItemsRead'],
        href: '/store/orderable-items',
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'historicalOrders',
        icon: <LocalShippingOutlined />,
        href: '/store/orders-history',
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'orderViaForm',
        icon: <UpdateOutlined />,
        href: '/store/order-planning',
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'bulkOrder',
    icon: <PackageCheck />,
    permissions: ['BulkOrdersRead'],
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'bulkOrderManagement',
        icon: <ListIcon />,
        href: '/bulk-order-management',
        permissions: ['BulkOrdersRead'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'demandModifiers',
        icon: <UpdateOutlined />,
        href: '/demand-modifiers',
        permissions: ['BulkOrdersRead'],
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'receiveStock',
    icon: <LocalShippingOutlined />,
    permissions: ['PurchaseOrderRead'],
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'purchaseOrder',
        icon: <ReceiptCheck />,
        href: '/store/goods-receipts',
        permissions: ['PurchaseOrderRead'],
        systemIds: [System.OrderlyForStores],
      }, {
        canRender: false,
        translationKey: 'invoices',
        icon: <FileCheck01 />,
        href: '/invoices',
        permissions: ['InvoiceReconciliationRead', 'InvoiceReconciliationWrite', 'InvoiceReconciliationAdmin'],
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'transferStock',
    icon: <SwapCallsOutlined />,
    permissions: ['TransferRead'],
    href: '/store/stock-transfers',
    systemIds: [System.OrderlyForStores],
  },
  {
    canRender: false,
    translationKey: 'wasteStock',
    icon: <Trash03 />,
    href: '/store/waste-logs',
    permissions: ['DailyWasteCheckSheetRead'],
    systemIds: [System.OrderlyForStores],
  },
  {
    canRender: false,
    translationKey: 'count',
    icon: <Cube />,
    permissions: ['StockRead', 'StockCountAdmin', 'StockCountRead', 'StockLocationRead'],
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'stockCount',
        icon: <PackageCheck />,
        href: '/store/stock-count',
        permissions: ['StockCountAdmin', 'StockCountRead'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'stockOnHand',
        icon: <CoinsHand />,
        permissions: ['StockRead'],
        href: '/store/stock',
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'countZones',
        icon: <MarkerPin01 />,
        href: '/store/stock-count-zones',
        permissions: ['StockLocationRead'],
        storePermission: 'LocationsEnabled',
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'transactions',
    icon: <CoinsSwap02 />,
    permissions: ['TransactionAdmin', 'TransactionRead', 'POSMapping'],
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'transactions',
        icon: <CoinsSwap02 />,
        href: '/transactions',
        permissions: ['TransactionAdmin', 'TransactionRead'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'itemMapping',
        icon: <SwapHoriz />,
        href: '/manage-item-mapping',
        permissions: ['POSMapping'],
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'cashManagement',
    icon: <CoinsHand />,
    permissions: ['CashManagementView', 'CashManagementCreate', 'CashManagementEdit', 'CashManagementDelete', 'CashManagementReconcile', 'CashReconciliationRead'],
    systemIds: [System.OrderlyForStores],
    status: 'demo',
    children: [
      {
        canRender: false,
        translationKey: 'cashOverview',
        icon: <CoinsHand />,
        permissions: ['CashManagementView', 'CashManagementCreate', 'CashManagementEdit', 'CashManagementDelete', 'CashManagementReconcile'],
        href: '/cash-management',
        systemIds: [System.OrderlyForStores],
        status: 'demo',
      },
      {
        canRender: false,
        translationKey: 'cashReconciliation',
        icon: <CoinsSwap02 />,
        permissions: ['CashReconciliationRead'],
        href: '/cash-reconciliation',
        systemIds: [System.OrderlyForStores],
        status: 'demo',
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'rangePlanning',
    icon: <LineStyle />,
    status: 'preview',
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'viewRanges',
        icon: <DashboardOutlined />,
        href: '/range/ranges',
        permissions: ['RangeRead'],
        status: 'preview',
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'viewMenus',
        icon: <MenuBookOutlined />,
        href: '/range/menus',
        permissions: ['MenuRead'],
        status: 'preview',
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'products',
    icon: <FastfoodOutlined />,
    systemIds: [System.OrderlyForStores],
    children: [
      {
        canRender: false,
        translationKey: 'viewItems',
        icon: <FastfoodOutlined />,
        href: '/store/products?allItems=true',
        permissions: ['ItemRead'],
        systemIds: [System.OrderlyForStores],
      },
      {
        canRender: false,
        translationKey: 'viewRecipes',
        icon: <KitchenOutlined />,
        href: '/store/recipes',
        permissions: ['RecipeRead'],
        systemIds: [System.OrderlyForStores],
        hideInDemo: true,
      },
      {
        canRender: false,
        translationKey: 'viewRecipesV2',
        icon: <KitchenOutlined />,
        href: '/store/recipes-v2',
        permissions: ['RecipeV2Read'],
        systemIds: [System.OrderlyForStores],
        status: 'demo',
      },
      {
        canRender: false,
        translationKey: 'dataQuality',
        icon: <ScaleOutlined />,
        href: '/store/data-quality',
        permissions: ['RecipeV2RationaliseRead'],
        systemIds: [System.OrderlyForStores],
        status: 'demo',
      },
      {
        canRender: false,
        translationKey: 'uomConversions',
        icon: <ScaleOutlined />,
        href: '/uom-conversions',
        permissions: ['ConversionRead'],
        systemIds: [System.OrderlyForStores],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'tasks',
    icon: <Checklist />,
    permissions: ['OperationalDaysViewDashboard', 'AdvancedTasksView'],
    systemIds: [System.OrderlyForStores],
    status: 'demo',
    children: [
      {
        canRender: false,
        translationKey: 'operationalDay',
        icon: <LineStyle />,
        href: '/operational-day',
        permissions: ['OperationalDaysViewDashboard'],
        systemIds: [System.OrderlyForStores],
        status: 'demo',
      },
      {
        canRender: false,
        translationKey: 'taskDashboard',
        icon: <AssignmentTurnedIn />,
        href: '/task-reporting',
        permissions: ['AdvancedTasksView'],
        systemIds: [System.OrderlyForStores],
        status: 'demo',
      },
      {
        canRender: false,
        translationKey: 'tasks',
        icon: <FormatListBulleted />,
        href: '/tasks',
        permissions: ['AdvancedTasksView'],
        systemIds: [System.OrderlyForStores],
        status: 'demo',
      },
    ],
  },
  // Compliance Module
  {
    canRender: false,
    translationKey: 'complianceDashboard',
    icon: <DashboardOutlined />,
    href: '/compliance/dashboard',
    permissions: ['ViewComplianceDashboard'],
    systemIds: [System.OrderlyForCompliance],
  },
  {
    canRender: false,
    translationKey: 'audits',
    icon: <AssessmentOutlined />,
    permissions: ['AuditsView'],
    systemIds: [System.OrderlyForCompliance],
    children: [
      {
        canRender: false,
        translationKey: 'auditTypes',
        icon: <ListIcon />,
        href: '/compliance/audits/types',
        permissions: ['AuditTypesView'],
        systemIds: [System.OrderlyForCompliance],
      },
      {
        canRender: false,
        translationKey: 'auditTemplates',
        icon: <FileCheck01 />,
        href: '/compliance/audits/templates',
        permissions: ['AuditTemplatesView'],
        systemIds: [System.OrderlyForCompliance],
      },
      {
        canRender: false,
        translationKey: 'auditSchedules',
        icon: <BarChart08 />,
        href: '/compliance/audits/schedules',
        permissions: ['ScheduledAuditsCreate'],
        systemIds: [System.OrderlyForCompliance],
      },
      {
        canRender: false,
        translationKey: 'myAudits',
        icon: <AssessmentOutlined />,
        href: '/compliance/audits/history',
        permissions: ['AuditsView'],
        systemIds: [System.OrderlyForCompliance],
      },
      {
        canRender: false,
        translationKey: 'actionItems',
        icon: <AssignmentTurnedInOutlined />,
        href: '/compliance/audits/action-items',
        permissions: ['ActionItemsView'],
        systemIds: [System.OrderlyForCompliance],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'training',
    icon: <SchoolOutlined />,
    permissions: ['ViewTraining'],
    systemIds: [System.OrderlyForCompliance],
    children: [
      {
        canRender: false,
        translationKey: 'trainingCategories',
        icon: <ListIcon />,
        href: '/compliance/training/categories',
        permissions: ['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer'],
        systemIds: [System.OrderlyForCompliance],
      },
      {
        canRender: false,
        translationKey: 'trainingModules',
        icon: <MenuBookOutlined />,
        href: '/compliance/training/modules',
        permissions: ['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer'],
        systemIds: [System.OrderlyForCompliance],
      },
      {
        canRender: false,
        translationKey: 'trainingCoursesPacks',
        icon: <FolderOutlined />,
        href: '/compliance/training/courses-packs',
        permissions: ['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer'],
        systemIds: [System.OrderlyForCompliance],
        status: 'preview',
      },
      {
        canRender: false,
        translationKey: 'myTraining',
        icon: <SchoolOutlined />,
        href: '/compliance/training/my-training',
        permissions: ['TrainingAccess'],
        systemIds: [System.OrderlyForCompliance],
      },
      {
        canRender: false,
        translationKey: 'trainingAssignments',
        icon: <AssignmentTurnedInOutlined />,
        href: '/compliance/training/assignments',
        permissions: ['TrainingAdmin', 'TrainingAssignmentViewer', 'TrainingAssignmentManager'],
        systemIds: [System.OrderlyForCompliance],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'complianceReports',
    icon: <BarChart08 />,
    href: '/compliance/reports',
    permissions: ['ViewComplianceReports', 'TrainingSummaryReportRead', 'TrainingCompletionReportRead'],
    systemIds: [System.OrderlyForCompliance],
  },
  //compliance for non-compliance users
  {
    canRender: false,
    translationKey: 'compliance',
    icon: <FileCheck01 />,
    permissions: ['AuditsView'],
    systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
    children: [
      {
        canRender: false,
        translationKey: 'complianceDashboard',
        icon: <DashboardOutlined />,
        href: '/compliance/dashboard',
        permissions: ['ViewComplianceDashboard'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'auditTypes',
        icon: <ListIcon />,
        href: '/compliance/audits/types',
        permissions: ['AuditTypesView'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'auditTemplates',
        icon: <FileCheck01 />,
        href: '/compliance/audits/templates',
        permissions: ['AuditTemplatesView'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'auditSchedules',
        icon: <BarChart08 />,
        href: '/compliance/audits/schedules',
        permissions: ['ScheduledAuditsCreate'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'myAudits',
        icon: <AssessmentOutlined />,
        href: '/compliance/audits/history',
        permissions: ['AuditsView'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'actionItems',
        icon: <AssignmentTurnedInOutlined />,
        href: '/compliance/audits/action-items',
        permissions: ['ActionItemsView'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'trainingCategories',
        icon: <ListIcon />,
        href: '/compliance/training/categories',
        permissions: ['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'trainingModules',
        icon: <MenuBookOutlined />,
        href: '/compliance/training/modules',
        permissions: ['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'trainingCoursesPacks',
        icon: <FolderOutlined />,
        href: '/compliance/training/courses-packs',
        permissions: ['TrainingAdmin', 'TrainingContentManager', 'TrainingContentViewer'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
        status: 'preview',
      },
      {
        canRender: false,
        translationKey: 'myTraining',
        icon: <SchoolOutlined />,
        href: '/compliance/training/my-training',
        permissions: ['TrainingAccess'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'trainingAssignments',
        icon: <AssignmentTurnedInOutlined />,
        href: '/compliance/training/assignments',
        permissions: ['TrainingAdmin', 'TrainingAssignmentViewer', 'TrainingAssignmentManager'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
      {
        canRender: false,
        translationKey: 'complianceReports',
        icon: <BarChart08 />,
        href: '/compliance/reports',
        permissions: ['ViewComplianceReports', 'TrainingSummaryReportRead', 'TrainingCompletionReportRead'],
        systemIds: [System.OrderlyForStores, System.OrderlyForDistributors],
      },
    ],
  },
  {
    canRender: false,
    translationKey: 'reporting',
    icon: <BarChart08 />,
    permissions: [...allReportPermissions],
    systemIds: [System.OrderlyForStores, System.OrderlyForCompliance],
    children: [
      {
        canRender: false,
        translationKey: 'reporting',
        icon: <BarChart08 />,
        href: '/reports',
        permissions: [...allReportPermissions],
        previewReplacementConfig: 'ReportsV2Enabled',
        systemIds: [System.OrderlyForStores, System.OrderlyForCompliance],
      },
      {
        canRender: false,
        translationKey: 'reportingV2',
        icon: <BarChart08 />,
        href: '/reports/v2',
        permissions: [...allReportPermissions],
        // Config-gated: only surfaces when the ReportsV2Enabled flag is true for the store.
        storePermission: 'ReportsV2Enabled',
        // Preview-gated too: non-production environments (or Admin) only until GA.
        status: 'preview',
        systemIds: [System.OrderlyForStores, System.OrderlyForCompliance],
      },
    ],
  }];


const systemNavItems: DrawerNavItem[] = [{
  canRender: true,
  translationKey: 'userSettings',
  icon: <ManageAccountsOutlined />,
  systemIds: [System.OrderlyForStores, System.OrderlyForDistributors, System.OrderlyForCompliance],
  children: [
    {
      canRender: true,
      translationKey: 'userSettings',
      icon: <ManageAccountsOutlined />,
      href: '/user-settings',
      systemIds: [System.OrderlyForStores, System.OrderlyForDistributors, System.OrderlyForCompliance],
    },
    {
      canRender: true,
      translationKey: 'notificationPreferences',
      icon: <ManageAccountsOutlined />,
      href: '/user-settings/notifications',
      systemIds: [System.OrderlyForStores, System.OrderlyForDistributors, System.OrderlyForCompliance],
    },
  ],
}, {
  canRender: true,
  translationKey: 'documents',
  icon: <FolderOutlined />,
  href: '/documents',
  permissions: ['DocumentsView'],
  status: 'demo',
  systemIds: [System.OrderlyForStores, System.OrderlyForDistributors, System.OrderlyForCompliance],
}, {
  canRender: true,
  translationKey: 'settings',
  icon: <Settings01 />,
  href: '/settings',
  systemIds: [System.OrderlyForStores, System.OrderlyForDistributors, System.OrderlyForCompliance],
},
];

export const useDrawer = () => {
  const { getConfigurations } = useNswagClient();
  const theme = useTheme();
  const { selectedStore, hasPermissionTo, user, selectedSystem, systems } = useContext(UserContext);
  const [storeConfig, setStoreConfig] = useState<Configuration[]>([]);
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedNavItem, setSelectedNavItem] = useState<DrawerNavItem>();
  const slidingSubMenuWidth = 320;
  const { logError } = useLogError();
  const [appNavItemsToUse, setAppNavItemsToUse] = useState<DrawerNavItem[]>([]);
  const [systemNavItemsToUse, setSystemNavItemsToUse] = useState<DrawerNavItem[]>([]);

  useEffect(() => {
    if (selectedSystem) {

      const updatedNavItems = appNavItems.map((item) => {
        checkPermissions(item, storeConfig);
        return item;
      });

      setAppNavItemsToUse(updatedNavItems);

      const updatedSystemNavItems = systemNavItems.map((item) => {
        checkPermissions(item, storeConfig);
        return item;
      });

      setSystemNavItemsToUse(updatedSystemNavItems);

    } else {
      setAppNavItemsToUse([]);
      setSystemNavItemsToUse([]);
    }
  }, [selectedSystem, storeConfig]);

  const handleNavItemClick = (item: DrawerNavItem) => {
    if (item.href || ((item.children?.length ?? 0) > 0)) {
      const renderableChildren = item.children?.filter(c => c.canRender !== false);

      // If only one child is accessible, navigate directly to it
      if (renderableChildren?.length === 1 && renderableChildren[0].href) {
        setSelectedNavItem(undefined);
        navigate(renderableChildren[0].href);
        return;
      }

      setSelectedNavItem((prev) => {
        if (!item?.children) return undefined;
        return prev?.translationKey === item.translationKey ? undefined : item;
      });

      if (!item.children) {
        navigate(item.href ?? '/');
      }
    }
  };

  useEffect(() => {
    if (selectedStore) {
      getConfigurations(selectedStore.franchiseName ?? '', selectedStore.storeNumber ?? '')
        .then((result: SuccessResponse_1OfGetConfigurationResponse) => {
          setStoreConfig(result.data?.configurations ?? []);
        })
        .catch((error) => logError(error));
    }
  }, [selectedStore, user]);

  const hasStorePermission = (per: string | undefined, config: Configuration[]): boolean => {
    if (per === undefined) return true;

    return config.some(x => x.name === per && x.value === 'true');
  };

  const checkPermissions = (item: DrawerNavItem, configurations: Configuration[]): boolean => {
    // Determine if the current item can render
    const childPermissions = item.children?.flatMap((child) => child.permissions ?? []) ?? [];
    const userCanAccessPageOrSubPage = !item.permissions || hasPermissionTo(item.permissions) || hasPermissionTo(childPermissions);
    const userCanAccessSystem = item.systemIds?.some((systemId) => systemId === selectedSystem?.id) ?? false;
    const hostname = typeof window === 'undefined' ? undefined : window.location.hostname;
    const hasAdminPermission = hasPermissionTo(['Admin']);
    const storePermissionEnabled = hasStorePermission(item.storePermission, configurations);
    const previewSwapMode = resolvePreviewSwap(hostname, storePermissionEnabled, hasAdminPermission);
    const storeCanAccessPage = !item.storePermission ||
      (item.status === 'preview' ? previewSwapMode !== 'legacy' : storePermissionEnabled);
    const hasStatusAccess = canAccessPageStatus(item.status, {
      hostname,
      hasAdminPermission,
      hasPreviewConfiguration: item.status === 'preview' && !!item.storePermission && storePermissionEnabled,
    });
    // Demo Mode bypasses permission/system/store checks entirely for demo-flagged items —
    // hasStatusAccess already requires Demo Mode to be enabled for 'demo' status items.
    const bypassForDemo = item.status === 'demo' && hasStatusAccess;
    // Some items should be hidden entirely while Demo Mode is enabled (e.g. superseded by a
    // demo equivalent) — this takes priority over every other check.
    const hiddenByDemo = shouldHideInDemo(item.hideInDemo);
    const hiddenByPreviewReplacement = !!item.previewReplacementConfig &&
      resolvePreviewSwap(
        hostname,
        hasStorePermission(item.previewReplacementConfig, configurations),
        hasAdminPermission,
      ) === 'preview';

    const canRender = !hiddenByDemo && !hiddenByPreviewReplacement &&
      (bypassForDemo || (userCanAccessPageOrSubPage && storeCanAccessPage && userCanAccessSystem && hasStatusAccess));

    item.canRender = canRender;

    item.children?.forEach(i => checkPermissions(i, configurations));

    return item.canRender;
  };

  const isHighLighted = (item: DrawerNavItem, pathname: string, search: string) => {
    const urlEquals: boolean = item.children ? item.children.some(i => i?.href === pathname) : item?.href === pathname;

    const fullUrl = pathname + search;
    let urlstarts: boolean = item.children ? item.children.some(i => fullUrl.startsWith(i?.href ?? '-')) : fullUrl.startsWith(item.href ?? '-');

    if (search === '' || item.href === '/') {
      urlstarts = false;
    }

    return urlEquals || urlstarts;
  };

  return { storeConfig, open, setOpen, isMobile, isHighLighted, selectedNavItem, setSelectedNavItem, appNavItemsToUse, handleNavItemClick, systemNavItemsToUse, slidingSubMenuWidth };
};
